import type { ComponentProps, MouseEventHandler, PropsWithChildren, ReactElement, ReactNode } from 'react'
import { forwardRef, useEffect, useRef } from 'react'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import ModalBase from './ModalBase'
import ModalBody from './ModalBody'
import ModalContent from './ModalContent'
import type { ModalFooterOnClickHandler } from './ModalFooter'
import ModalFooter from './ModalFooter'
import ModalHeader from './ModalHeader'
import ModalImageThumbnail from './ModalImageThumbnail'

type InheritedModalHeaderProps = Partial<Pick<ComponentProps<typeof ModalHeader>, 'title' | 'subtitle'>>
type InheritedModalFooterProps = Pick<ComponentProps<typeof ModalFooter>, 'isPrimaryActionHidden' | 'isSecondaryActionHidden' | 'actionsLayout' | 'tabletActionsLayout'>
type InheritedModalImageThumbnailProps = Partial<Pick<ComponentProps<typeof ModalImageThumbnail>, 'image' | 'imageParams'>>
type InheritedModalContentProps = Pick<ComponentProps<typeof ModalContent>, 'flush' | 'flushVerticalTop' | 'flushListItems'>

interface Props extends ComponentProps<typeof ModalBase>, InheritedModalHeaderProps, InheritedModalFooterProps, InheritedModalImageThumbnailProps, InheritedModalContentProps {
  loading?: boolean
  isPrimaryActionDisabled?: boolean
  isSecondaryActionDisabled?: boolean
  isHeaderOverlay?: boolean
  /**
   * If provided, the primary action will be displayed.
   */
  primaryActionText?: ReactElement | string
  /**
   * If provided, the secondary action will be displayed.
   */
  secondaryActionText?: ReactElement | string
  primaryActionHref?: string
  primaryActionTo?: string
  primaryActionTarget?: string
  secondaryActionTo?: string
  secondaryActionTarget?: string
  /**
   * Extra node at the bottom of the header.
   */
  headerExtension?: ReactNode
  /**
   * Callback on back button click.
   *
   * If provided, enables the back button to be displayed.
   */
  onBack?: MouseEventHandler<HTMLButtonElement>
  onPrimaryActionClick?: ModalFooterOnClickHandler
  onSecondaryActionClick?: ModalFooterOnClickHandler
}

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    image,
    imageParams,
    actionsLayout,
    tabletActionsLayout,
    isPrimaryActionDisabled,
    isPrimaryActionHidden,
    isHeaderOverlay,
    isSecondaryActionDisabled,
    isSecondaryActionHidden,
    loading,
    primaryActionText,
    primaryActionHref,
    primaryActionTarget,
    primaryActionTo,
    secondaryActionTo,
    secondaryActionText,
    secondaryActionTarget,
    subtitle,
    title,
    headerExtension,
    dismissible,
    onBack,
    onClose,
    onPrimaryActionClick,
    onSecondaryActionClick,
    flush,
    flushVerticalTop,
    flushListItems,
    'data-testid': dataTestId,
    ...rest
  } = props

  const modalContext = useModalElementContext()
  const prevLoadingRef = useRef<boolean | undefined>(loading)

  useEffect(() => {
    const wasLoading = prevLoadingRef.current
    prevLoadingRef.current = loading

    if (wasLoading && !loading) {
      modalContext?.resolve()
    }
  }, [loading, modalContext])

  return <ModalBase
    {...rest}
    data-testid={dataTestId}
    onClose={onClose}
    dismissible={dismissible}
  >
    <LoadingIndicator visible={!!loading} floating />
    <ModalHeader
      title={title ?? ''}
      subtitle={subtitle}
      isOverlay={isHeaderOverlay}
      onBackButtonClick={onBack}
      onCloseButtonClick={onClose}
      dismissible={dismissible}
      data-testid={dataTestId ? `${dataTestId}-header` : undefined}
    >
      {headerExtension}
    </ModalHeader>
    <ModalBody ref={ref}>
      {image && <ModalImageThumbnail image={image} imageParams={imageParams} />}
      {!!children && <ModalContent flush={flush} flushVerticalTop={flushVerticalTop} flushListItems={flushListItems}>
        {children}
      </ModalContent>}
    </ModalBody>
    {(primaryActionText || secondaryActionText) && <ModalFooter
      data-testid={dataTestId ? `${dataTestId}-footer` : undefined}
      actionsLayout={actionsLayout}
      tabletActionsLayout={tabletActionsLayout}
      isPrimaryActionHidden={isPrimaryActionHidden}
      isSecondaryActionHidden={isSecondaryActionHidden}
      primaryActionProps={{
        children: primaryActionText,
        disabled: isPrimaryActionDisabled,
        onClick: onPrimaryActionClick,
        href: primaryActionHref,
        to: primaryActionTo,
        target: primaryActionTarget,
      }}
      secondaryActionProps={{
        children: secondaryActionText,
        disabled: isSecondaryActionDisabled,
        onClick: onSecondaryActionClick,
        to: secondaryActionTo,
        target: secondaryActionTarget,
      }}
    />}
  </ModalBase>
})

export default Modal
